import { t, Trans } from '@lingui/macro'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { backupCollateralAtom } from 'components/rtoken-setup/atoms'
import { useAtom, useAtomValue } from 'jotai'
import { cn } from '@/lib/utils'
import {
  autoRegisterBackupAssetsAtom,
  backupChangesAtom,
  isNewBackupProposedAtom,
} from '../atoms'
import {
  CollateralChange,
  DiversityFactorChange,
} from '../hooks/useBackupChanges'
import { ParameterChangePreview } from './ItemPreview'
import ListItemPreview from './ListItemPreview'
import PreviewBox from './PreviewBox'

interface Props {
  className?: string
}

const ProposedBackupPreview = ({ className }: Props) => {
  const [isNewBackupProposed, setProposeNewBackup] = useAtom(
    isNewBackupProposedAtom
  )
  const { count, diversityFactor, collateralChanges, priorityChanges } =
    useAtomValue(backupChangesAtom)
  const [proposedBackup, setProposedBackup] = useAtom(backupCollateralAtom)
  const [autoRegister, setAutoRegister] = useAtom(autoRegisterBackupAssetsAtom)

  if (!isNewBackupProposed) {
    return null
  }

  const handleRevertDiversity = (change: DiversityFactorChange) => {
    setProposedBackup({
      ...proposedBackup,
      [change.target]: {
        diversityFactor: change.current,
        collaterals: proposedBackup[change.target].collaterals,
      },
    })
  }

  const handleRevertCollateral = (change: CollateralChange) => {
    const proposedBasket = proposedBackup[change.collateral.targetName] || {}

    if (change.isNew) {
      const index = proposedBasket.collaterals.findIndex(
        (c) => c.address === change.collateral.address
      )

      setProposedBackup({
        ...proposedBackup,
        [change.collateral.targetName]: {
          diversityFactor:
            proposedBasket.diversityFactor > 0
              ? proposedBasket.diversityFactor - 1
              : 0,
          collaterals: [
            ...proposedBasket.collaterals.slice(0, index),
            ...proposedBasket.collaterals.slice(index + 1),
          ],
        },
      })
    } else {
      setProposedBackup({
        ...proposedBackup,
        [change.collateral.targetName]: {
          diversityFactor:
            proposedBackup[change.collateral.targetName].diversityFactor + 1,
          collaterals: [...proposedBasket.collaterals, change.collateral],
        },
      })
    }
  }

  return (
    <>
      <div
        className={cn(
          'border border-border rounded-xl p-6',
          className
        )}
      >
        <div className="flex items-center">
          <span className="font-semibold text-warning">
            <Trans>New backup configuration</Trans>
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setProposeNewBackup(false)}
            className="ml-auto"
          >
            <Trans>Revert</Trans>
          </Button>
        </div>
        <label>
          <div className="flex items-center mt-2 gap-2">
            <Checkbox
              checked={autoRegister}
              onCheckedChange={() => setAutoRegister(!autoRegister)}
            />
            <span className="font-semibold">Generate Asset Registry calls</span>
          </div>
        </label>
      </div>
      {!!count && (
        <PreviewBox
          className="border border-border rounded-xl p-6 mt-6"
          count={count}
          title={t`Backup basket`}
        >
          {diversityFactor.map((change) => (
            <ParameterChangePreview
              className="mt-4"
              title={t`Change diversity factor`}
              subtitle={change.target}
              current={change.current.toString()}
              proposed={change.proposed.toString()}
              onRevert={() => handleRevertDiversity(change)}
              key={change.target}
            />
          ))}
          {collateralChanges.map((change) => (
            <ListItemPreview
              className="mt-4"
              isNew={change.isNew}
              onRevert={() => handleRevertCollateral(change)}
              label={change.collateral.symbol}
              key={change.collateral.symbol}
            />
          ))}
          {priorityChanges.map((change) => (
            <ParameterChangePreview
              key={change.collateral.address}
              className="mt-4"
              title={t`Change diversity factor`}
              subtitle={change.collateral.symbol}
              current={change.current.toString()}
              proposed={change.proposed.toString()}
            />
          ))}
        </PreviewBox>
      )}
    </>
  )
}

export default ProposedBackupPreview
